import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import PropTypes from 'prop-types'
import { useState } from 'react'
import Swal from 'sweetalert2'
import { useAppServices } from 'hook/services'
import {
  AUDIENCES,
  BLUE,
  PLACEMENTS,
  TYPES,
  blueSwitchSx,
  isoToSydneyLocal,
  pathsToText,
  sydneyLocalToISO,
  textToPaths,
} from '../utils'

const emptyForm = {
  name: '',
  type: 'popup',
  placement: 'center',
  audience: 'both',
  title: '',
  message: '',
  pages: '',
  excludePages: '',
  cta: { enabled: true, label: '', link: '', newTab: false },
  startAt: '',
  endAt: '',
  dismissible: true,
  delaySeconds: 0,
  autoCloseSeconds: 0,
  priority: 0,
  isActive: true,
}

const toForm = (notification) => {
  if (!notification) return emptyForm

  return {
    ...emptyForm,
    ...notification,
    pages: pathsToText(notification.pages),
    excludePages: pathsToText(notification.excludePages),
    cta: { ...emptyForm.cta, ...(notification.cta || {}) },
    startAt: isoToSydneyLocal(notification.startAt),
    endAt: isoToSydneyLocal(notification.endAt),
  }
}

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    color="text.secondary"
    sx={{ letterSpacing: 1, display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
)

SectionLabel.propTypes = { children: PropTypes.node.isRequired }

export default function NotificationFormModal({ notification, handleRefresh }) {
  const isEdit = Boolean(notification)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState(emptyForm)
  const AppService = useAppServices()

  const handleOpen = () => {
    setForm(toForm(notification))
    setError('')
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }))
  const onText = (field) => (e) => setField(field)(e.target.value)
  const setNested = (group, field) => (value) =>
    setForm((prev) => ({ ...prev, [group]: { ...prev[group], [field]: value } }))

  // Type drives which placements make sense, so reset to the first valid one.
  const handleTypeChange = (e) => {
    const type = e.target.value
    setForm((prev) => ({ ...prev, type, placement: PLACEMENTS[type][0].value }))
  }

  const validate = () => {
    if (!form.title && !form.message) return 'Add a title or a message — the website needs something to show.'
    if (!form.startAt || !form.endAt) return 'Start time and end time are both required.'
    if (new Date(sydneyLocalToISO(form.endAt)) <= new Date(sydneyLocalToISO(form.startAt))) {
      return 'End time must be after start time.'
    }
    if (form.cta.enabled && !form.cta.link) return 'Add a CTA link or turn the CTA off.'
    return ''
  }

  const handleSave = async () => {
    const message = validate()
    if (message) {
      setError(message)
      return
    }

    setSaving(true)
    setError('')

    const payload = {
      ...form,
      pages: textToPaths(form.pages),
      excludePages: textToPaths(form.excludePages),
      startAt: sydneyLocalToISO(form.startAt),
      endAt: sydneyLocalToISO(form.endAt),
      priority: Number(form.priority) || 0,
      delaySeconds: Number(form.delaySeconds) || 0,
      autoCloseSeconds: Number(form.autoCloseSeconds) || 0,
    }

    const request = isEdit
      ? AppService.notifications.update({ payload: { ...payload, id: notification._id } })
      : AppService.notifications.create({ payload })

    const { response, error: apiError } = await request

    setSaving(false)

    if (!response) {
      setError(apiError?.message || 'Could not save the notification. Please try again.')
      return
    }

    setOpen(false)
    handleRefresh()
    Swal.fire({
      icon: 'success',
      title: isEdit ? 'Notification Updated!' : 'Notification Created!',
      timer: 2000,
      showConfirmButton: false,
    })
  }

  return (
    <>
      {isEdit ? (
        <Tooltip title="Edit notification">
          <IconButton size="small" onClick={handleOpen} sx={{ color: BLUE.main }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          sx={{
            backgroundColor: '#fff',
            color: BLUE.main,
            fontWeight: 700,
            fontSize: '0.8rem',
            borderRadius: '8px',
            textTransform: 'none',
            px: 2,
            '&:hover': { backgroundColor: BLUE.soft },
          }}
        >
          New Notification
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${BLUE.main} 0%, ${BLUE.dark} 100%)`,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3,
          }}
        >
          <Typography variant="h6" fontWeight={700} style={{ color: 'white' }} fontSize="1rem">
            {isEdit ? 'Edit Notification' : 'Build a Notification'}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>}

          <Grid container spacing={4} mt={0}>
            <Grid item xs={12}>
              <SectionLabel>SETUP</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Internal name"
                    fullWidth
                    size="small"
                    value={form.name}
                    onChange={onText('name')}
                    placeholder="e.g. August host boost promo"
                    helperText="Only for this list — never shown on the website"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Display as"
                    fullWidth
                    size="small"
                    value={form.type}
                    onChange={handleTypeChange}
                  >
                    {TYPES.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Position"
                    fullWidth
                    size="small"
                    value={form.placement}
                    onChange={onText('placement')}
                  >
                    {PLACEMENTS[form.type].map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Show to"
                    fullWidth
                    size="small"
                    value={form.audience}
                    onChange={onText('audience')}
                  >
                    {AUDIENCES.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <SectionLabel>CONTENT</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Title"
                    fullWidth
                    size="small"
                    value={form.title}
                    onChange={onText('title')}
                    placeholder="Exclusive Aloca Bonus"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Message"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={form.message}
                    onChange={onText('message')}
                    placeholder="50% off Shoot Location Success — this week only."
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <SectionLabel>CALL TO ACTION</SectionLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.cta.enabled}
                    sx={blueSwitchSx}
                    onChange={(e) => setNested('cta', 'enabled')(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Show a CTA button
                  </Typography>
                }
              />
              {form.cta.enabled && (
                <Grid container spacing={2} mt={0}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Button label"
                      fullWidth
                      size="small"
                      value={form.cta.label}
                      onChange={(e) => setNested('cta', 'label')(e.target.value)}
                      placeholder="Learn more"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Link"
                      fullWidth
                      size="small"
                      value={form.cta.link}
                      onChange={(e) => setNested('cta', 'link')(e.target.value)}
                      placeholder="/search or https://..."
                      helperText="Internal paths route in-app; full URLs open as normal links"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.cta.newTab}
                          sx={blueSwitchSx}
                          onChange={(e) => setNested('cta', 'newTab')(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Open in a new tab
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              <SectionLabel>SCHEDULE — ALL TIMES IN SYDNEY (AEST/AEDT)</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start time"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    value={form.startAt}
                    onChange={onText('startAt')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="End time"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    value={form.endAt}
                    onChange={onText('endAt')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <SectionLabel>WHERE IT SHOWS</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Only on these pages"
                    fullWidth
                    size="small"
                    value={form.pages}
                    onChange={onText('pages')}
                    placeholder="/, /search, /host/*"
                    helperText="Comma separated. Leave blank for every page."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Never on these pages"
                    fullWidth
                    size="small"
                    value={form.excludePages}
                    onChange={onText('excludePages')}
                    placeholder="/sign-in, /sign-up"
                    helperText="Wins over the list on the left."
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <SectionLabel>BEHAVIOUR</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Priority"
                    type="number"
                    fullWidth
                    size="small"
                    value={form.priority}
                    onChange={onText('priority')}
                    helperText="Higher wins when several qualify at once"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Delay before showing (seconds)"
                    type="number"
                    fullWidth
                    size="small"
                    value={form.delaySeconds}
                    onChange={onText('delaySeconds')}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Auto close after (seconds)"
                    type="number"
                    fullWidth
                    size="small"
                    value={form.autoCloseSeconds}
                    onChange={onText('autoCloseSeconds')}
                    inputProps={{ min: 0 }}
                    helperText="0 = stays until the visitor closes it"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.dismissible}
                        sx={blueSwitchSx}
                        onChange={(e) => setField('dismissible')(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        Visitors can close it
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        sx={blueSwitchSx}
                        onChange={(e) => setField('isActive')(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        Active (runs on schedule)
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3, gap: 1 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: `linear-gradient(135deg, ${BLUE.main}, ${BLUE.dark})`,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
            style={{ color: 'white' }}
          >
            {saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : isEdit ? (
              'Save Changes'
            ) : (
              'Create Notification'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

NotificationFormModal.propTypes = {
  notification: PropTypes.object,
  handleRefresh: PropTypes.func.isRequired,
}

NotificationFormModal.defaultProps = { notification: null }
